'use client';

import React from 'react';

export default function AboutSection() {
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'linear-gradient(rgb(5, 5, 5), rgb(7, 9, 15), rgb(5, 5, 5))' }}
    >
      {/* Background orb */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: 'absolute', top: '20%', left: '-10%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div>
            <div className="reveal">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400 mb-4 block">Our Story</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
                Why <span className="gradient-text">Scalio AI?</span>
              </h2>
            </div>
            <div className="reveal reveal-delay-1">
              <p className="text-base text-neutral-400 leading-relaxed mb-5">
                Born from a passion for AI and a relentless drive to help brands grow without burning out — Scalio AI Agency was built for the creator economy.
              </p>
              <p className="text-base text-neutral-400 leading-relaxed mb-5">
                We believe every brand, regardless of size, deserves access to the growth systems used by the world&apos;s fastest-scaling companies. That&apos;s why we productize intelligence — turning complex AI tools into simple, powerful machines that work while you sleep.
              </p>
              <p className="text-base text-neutral-400 leading-relaxed">
                Our team lives at the intersection of artificial intelligence and content strategy. We don&apos;t just consult — we build, implement, and scale alongside you.
              </p>
            </div>
          </div>

          {/* Right: Cards */}
          <div className="space-y-5">
            {/* Differentiator 1 */}
            <div className="reveal reveal-delay-2 glass-card rounded-2xl p-6 flex gap-4 service-card">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', color: 'rgb(59, 130, 246)' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
                  <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
                  <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
                  <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
                  <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
                  <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
                  <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
                  <path d="M6 18a4 4 0 0 1-1.967-.516" />
                  <path d="M19.967 17.484A4 4 0 0 1 18 18" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-white mb-1.5">AI-First Approach</h4>
                <p className="text-sm text-neutral-500 leading-relaxed">Every strategy is built on AI tools and data — not guesswork or gut feeling.</p>
              </div>
            </div>

            {/* Differentiator 2 */}
            <div className="reveal reveal-delay-3 glass-card rounded-2xl p-6 flex gap-4 service-card">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', color: 'rgb(59, 130, 246)' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-white mb-1.5">Creator Economy Native</h4>
                <p className="text-sm text-neutral-500 leading-relaxed">We live inside the creator space. We know what works on TikTok, IG, and beyond.</p>
              </div>
            </div>

            {/* Differentiator 3 */}
            <div className="reveal reveal-delay-4 glass-card rounded-2xl p-6 flex gap-4 service-card">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', color: 'rgb(59, 130, 246)' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-white mb-1.5">Results or Nothing</h4>
                <p className="text-sm text-neutral-500 leading-relaxed">We measure success in tangible outcomes: views, revenue, and time saved — period.</p>
              </div>
            </div>

            {/* Stats */}
            <div className="reveal reveal-delay-5 glass-card rounded-2xl p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-black gradient-text mb-1">3+</div>
                  <div className="text-xs text-neutral-600 font-medium">Years in AI</div>
                </div>
                <div>
                  <div className="text-2xl font-black gradient-text mb-1">100+</div>
                  <div className="text-xs text-neutral-600 font-medium">Brands Scaled</div>
                </div>
                <div>
                  <div className="text-2xl font-black gradient-text mb-1">4.9</div>
                  <div className="text-xs text-neutral-600 font-medium">Client Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
